import { useMemo, useState } from "react";
import { useProjectMemory } from "@/hooks/useProjectMemory";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  RefreshCw,
} from "lucide-react";

interface ActivityTabProps {
  projectId: string;
}

function formatRelativeTime(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return "just now";
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}

function fileKey(file: { drive_item_id?: string | null; file_name: string }) {
  return file.drive_item_id ?? file.file_name;
}

const ACTIVITY_WINDOW_DAYS = 30;
const MAX_DISPLAY_FILES = 20;

export default function ActivityTab({ projectId }: ActivityTabProps) {
  const { data: memory, isLoading, error } = useProjectMemory(projectId);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [showAllFiles, setShowAllFiles] = useState(false);

  // Only show files modified within the past 30 days
  const recentFiles = useMemo(() => {
    if (!memory?.file_activity) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - ACTIVITY_WINDOW_DAYS);
    return memory.file_activity
      .filter((file) => {
        if (!file.modified_at) return false;
        return new Date(file.modified_at) >= cutoff;
      })
      .sort((a, b) => {
        const aTime = a.modified_at ? new Date(a.modified_at).getTime() : 0;
        const bTime = b.modified_at ? new Date(b.modified_at).getTime() : 0;
        return bTime - aTime;
      });
  }, [memory?.file_activity]);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="rounded-lg p-6" style={{ backgroundColor: "var(--surface)" }}>
          <div className="h-4 rounded w-1/3 mb-3" style={{ backgroundColor: "var(--bg)" }} />
          <div className="h-3 rounded w-full mb-2" style={{ backgroundColor: "var(--bg)" }} />
          <div className="h-3 rounded w-4/5" style={{ backgroundColor: "var(--bg)" }} />
        </div>
      </div>
    );
  }

  if (error || !memory) {
    return (
      <div className="p-6">
        <div className="empty-state">
          <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--muted-foreground)" }} />
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            No activity data available yet. The intelligence agent will populate this after syncing.
          </p>
        </div>
      </div>
    );
  }

  const toggleExpanded = (key: string) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const syncMetadata = (
    <div className="flex items-center gap-3 text-xs" style={{ color: "var(--text-secondary)" }}>
      {memory.needs_agent_refresh && (
        <span
          className="flex items-center gap-1 font-medium px-1.5 py-0.5 rounded"
          style={{
            backgroundColor: "color-mix(in srgb, var(--accent) 12%, transparent)",
            color: "var(--accent)",
          }}
        >
          <RefreshCw className="w-3 h-3" />
          Needs refresh
        </span>
      )}
      {memory.last_graph_sync && (
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Synced {formatRelativeTime(memory.last_graph_sync)}
        </span>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6 overflow-auto h-full">
      {/* Empty state */}
      {recentFiles.length === 0 && (
        <div
          className="rounded-lg p-5"
          style={{
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border)",
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>File Activity</h3>
            {syncMetadata}
          </div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No file changes in the last 30 days</p>
        </div>
      )}

      {/* Unified file activity list */}
      {recentFiles.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
              File Activity
              <span className="ml-2 text-xs font-normal" style={{ color: "var(--text-secondary)" }}>
                {recentFiles.length} file{recentFiles.length !== 1 ? "s" : ""} in the last 30 days
              </span>
            </h3>
            {syncMetadata}
          </div>
          <div className="space-y-2">
            {(showAllFiles ? recentFiles : recentFiles.slice(0, MAX_DISPLAY_FILES)).map((file) => {
              const key = fileKey(file);
              const hasDiff = file.annotation && file.content_diff && file.content_diff.length > 0;
              const isExpanded = expandedFiles.has(key);

              return (
                <div
                  key={key}
                  className="rounded-lg text-sm"
                  style={{
                    backgroundColor: "var(--surface)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <div className="flex items-center gap-3 p-3">
                    <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate" style={{ color: "var(--text-primary)" }}>
                          {file.file_name}
                        </span>
                        {file.change_type && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{
                              backgroundColor:
                                file.change_type === "created"
                                  ? "color-mix(in srgb, var(--success) 15%, transparent)"
                                  : file.change_type === "deleted"
                                    ? "color-mix(in srgb, var(--error) 15%, transparent)"
                                    : "color-mix(in srgb, var(--info) 15%, transparent)",
                              color:
                                file.change_type === "created"
                                  ? "var(--success)"
                                  : file.change_type === "deleted"
                                    ? "var(--error)"
                                    : "var(--info)",
                            }}
                          >
                            {file.change_type}
                          </span>
                        )}
                      </div>
                      {file.annotation && (
                        <p className="text-xs mt-0.5 leading-snug" style={{ color: "var(--text-secondary)" }}>
                          {file.annotation}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                        {file.modified_by && <span>{file.modified_by}</span>}
                        {file.modified_at && (
                          <span>{formatRelativeTime(file.modified_at)}</span>
                        )}
                      </div>
                    </div>
                    {file.web_url && /^https?:\/\//i.test(file.web_url) && (
                      <a
                        href={file.web_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded transition-colors flex-shrink-0"
                        style={{ color: "var(--muted-foreground)" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--secondary)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>

                  {/* Expandable content diff */}
                  {hasDiff && (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(key)}
                        className="flex items-center gap-1 text-xs px-3 pb-2 transition-colors"
                        style={{ color: "var(--text-secondary)" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text-primary)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-secondary)"; }}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        See changes
                      </button>
                      {isExpanded && (
                        <div
                          className="mx-3 mb-3 rounded p-2 space-y-0.5 text-xs font-mono"
                          style={{ backgroundColor: "var(--bg)" }}
                        >
                          {file.content_diff!.map((entry, dIdx) => (
                            <div
                              key={dIdx}
                              className={
                                entry.type === "removed" ? "line-through" : ""
                              }
                              style={{
                                color:
                                  entry.type === "added"
                                    ? "var(--success)"
                                    : entry.type === "changed"
                                      ? "var(--warning)"
                                      : entry.type === "removed"
                                        ? "var(--error)"
                                        : "inherit",
                              }}
                            >
                              <span className="select-none mr-1.5 opacity-60">
                                {entry.type === "added"
                                  ? "+"
                                  : entry.type === "changed"
                                    ? "~"
                                    : entry.type === "removed"
                                      ? "-"
                                      : " "}
                              </span>
                              {entry.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {recentFiles.length > MAX_DISPLAY_FILES && (
            <button
              type="button"
              onClick={() => setShowAllFiles((prev) => !prev)}
              className="mt-2 text-xs font-medium hover:underline transition-colors"
              style={{ color: "var(--accent)" }}
            >
              {showAllFiles
                ? "Show fewer"
                : `Show ${recentFiles.length - MAX_DISPLAY_FILES} more files`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
